
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CreditCard, X, ShieldCheck, Loader2, Armchair, Accessibility, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SavedPassenger, UserLoyaltyAccount } from '@/api/entities';
import { User as UserEntity } from '@/api/entities';
import {
  SEAT_PREFERENCE_OPTIONS,
  getDefaultSeatPreference,
  normalizeSeatPreference,
} from '@/utils/seatPreference';
import { AIRLINE_PROGRAMS, getProgramByValue } from '@/constants/loyaltyPrograms';
import { TailLogo } from '@/components/profile/FrequentFlyerPrograms';
import { BRAND_PRIMARY_BTN_CLASS } from '@/constants/brandColors';
import WheelchairQuestions from './WheelchairQuestions';
import DobInput from './DobInput';
import { mentionsWheelchair, wheelchairComplete } from '../../../supabase/functions/_shared/wheelchair-assistance.js';
import { airlineGender } from '@/utils/passengerGender';
import { COUNTRIES } from '@/data/countries';
import {
  countryName,
  displayFirstName,
  missingTravelDocument,
  normalizeCountry,
  passportExpiryIssue,
  passportExpiryMessage,
  travelerVisaFlags,
  visaFlagMessage,
} from '@/utils/travelRequirements';

// Helper to safely format date strings for input[type=date]
// Uses UTC to prevent timezone-induced date shifts
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    console.warn("Invalid date string for formatting:", dateString, e);
    return '';
  }
};

// Shared chrome — pill-rounded, hairline border, royal-blue focus ring,
// 16px font on mobile (text-base) to defeat iOS auto-zoom, 14px on
// desktop (md:text-sm) for compactness. h-10 = 40px keeps the form
// compact while staying within an acceptable touch-target window
// (the surrounding tap area extends past the visible chrome via
// padding on the parent grid). The trailing
// `[&::-webkit-calendar-picker-indicator]:*` arbitrary variants
// recolor Chrome/Safari's native calendar glyph so it reads white on
// the dark UI instead of its default near-black; harmless on
// non-date inputs because the pseudo-element doesn't exist there.
const FIELD_BASE =
  'bg-white/[0.03] border-white/10 text-white placeholder:text-white/35 h-10 rounded-xl text-base md:text-sm font-normal px-3.5 transition-colors hover:bg-white/[0.05] focus-visible:ring-1 focus-visible:ring-[hsl(228,35%,58%)]/30 focus-visible:ring-offset-0 focus-visible:border-[hsl(228,35%,58%)]/30 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:hover:opacity-100';

// iOS Safari gives date inputs an intrinsic width that ignores
// `w-full` inside grid cells, so they bleed past the right edge on
// phones, and it centers the value text. `appearance-none` + `min-w-0`
// make the control respect the cell width, and the
// `-webkit-date-and-time-value` variant left-aligns the value to match
// the other fields. No effect on desktop browsers.
const DATE_FIELD = cn(
  FIELD_BASE,
  'w-full min-w-0 appearance-none [&::-webkit-date-and-time-value]:text-left'
);

const TRIGGER_BASE =
  'bg-white/[0.03] border-white/10 text-white h-10 rounded-xl text-base md:text-sm font-normal px-3.5 transition-colors hover:bg-white/[0.05] data-[placeholder]:text-white/35 data-[state=open]:bg-white/[0.06] data-[state=open]:border-[hsl(228,35%,58%)]/30 focus:ring-1 focus:ring-[hsl(228,35%,58%)]/30 focus:ring-offset-0 focus:border-[hsl(228,35%,58%)]/30';

const TEXTAREA_BASE =
  'bg-white/[0.03] border-white/10 text-white placeholder:text-white/35 rounded-xl text-base md:text-sm font-normal px-3.5 py-3 transition-colors hover:bg-white/[0.05] focus-visible:ring-1 focus-visible:ring-[hsl(228,35%,58%)]/30 focus-visible:ring-offset-0 focus-visible:border-[hsl(228,35%,58%)]/30 resize-none';

const EYEBROW = 'text-[10px] uppercase tracking-[0.22em] text-white/40 font-normal';
const FIELD_LABEL = 'text-[11px] font-normal text-white/55 tracking-wide';

// Small section wrapper — eyebrow + hairline divider above the grid.
function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2.5">
        <p className={EYEBROW}>{title}</p>
        <span className="flex-1 h-px bg-white/[0.06]" aria-hidden="true" />
      </div>
      {children}
    </div>
  );
}

// Single field cell — label, optional required marker, and the control.
function Field({ label, htmlFor, required, children, className = '' }) {
  return (
    <div className={cn('space-y-1.5 min-w-0', className)}>
      <Label htmlFor={htmlFor} className={FIELD_LABEL}>
        {label}
        {required && <span className="text-[hsl(228,30%,72%)] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

// A stored nationality or passport country as the picker's value: the
// country's name, or '' for a value that isn't a country (legacy free text
// such as "White"), so the picker shows its placeholder.
function countryPickerValue(value) {
  const code = normalizeCountry(value);
  return code ? countryName(code) : '';
}

function CountrySelect({ value, onChange, placeholder, ariaLabel }) {
  return (
    <Select value={countryPickerValue(value)} onValueChange={onChange}>
      <SelectTrigger className={TRIGGER_BASE} aria-label={ariaLabel}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {COUNTRIES.map(([code, name]) => (
          <SelectItem key={code} value={name}>{name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Visa and passport-expiry notes for one traveler on an international trip.
// Warnings only: the traveler can still book (visas are theirs to arrange),
// except a passport that expires before the trip ends, which the form blocks.
function TravelerTravelNotes({ passenger, requirement }) {
  if (!requirement?.international) return null;
  const who = displayFirstName(passenger.first_name);
  const { flags } = travelerVisaFlags({
    table: requirement.visaTable,
    nationality: passenger.nationality,
    passportCountry: passenger.passport_country,
    stops: requirement.stops,
  });
  const expiryIssue = passportExpiryIssue(passenger.passport_expiry, requirement.lastTravelDate);
  const warnings = [
    ...flags.filter((f) => f.needsAction || f.status === 'visa_on_arrival').map((f) => visaFlagMessage(f, who)),
    ...(expiryIssue ? [passportExpiryMessage(expiryIssue, who)] : []),
  ];
  const clear = flags.filter((f) => !f.needsAction && f.status !== 'visa_on_arrival').map((f) => visaFlagMessage(f, who));
  if (!warnings.length && !clear.length) return null;
  return (
    <div className="space-y-2 pt-1" aria-live="polite">
      {warnings.map((text) => (
        <div key={text} className="flex items-start gap-2.5 rounded-2xl border border-amber-300/20 bg-amber-400/[0.07] px-3.5 py-2.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-300 flex-shrink-0" aria-hidden="true" />
          <p className="text-[13px] leading-relaxed text-amber-100/85 font-normal">{text}</p>
        </div>
      ))}
      {clear.map((text) => (
        <div key={text} className="flex items-start gap-2.5 rounded-2xl border border-emerald-300/15 bg-emerald-400/[0.06] px-3.5 py-2.5">
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-300 flex-shrink-0" aria-hidden="true" />
          <p className="text-[13px] leading-relaxed text-emerald-100/80 font-normal">{text}</p>
        </div>
      ))}
      {warnings.length > 0 && (
        <p className="text-[11px] text-white/40 font-normal leading-relaxed">
          Entry rules change, so confirm on the destination&rsquo;s official government site before you fly.
        </p>
      )}
    </div>
  );
}

CountrySelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
};

const travelRequirementShape = PropTypes.shape({
  international: PropTypes.bool,
  stops: PropTypes.arrayOf(PropTypes.shape({ country: PropTypes.string, role: PropTypes.string })),
  lastTravelDate: PropTypes.string,
  visaTable: PropTypes.object,
});

TravelerTravelNotes.propTypes = {
  passenger: PropTypes.shape({
    first_name: PropTypes.string,
    nationality: PropTypes.string,
    passport_country: PropTypes.string,
    passport_expiry: PropTypes.string,
  }).isRequired,
  requirement: travelRequirementShape,
};

export default function PassengerForm({ selectedFlight, seatSelections = {}, passengers = [], onComplete, onCancel, isProcessing, travelRequirement = null }) {
  // International trips (flights touching more than one country) require
  // passport details; `travelRequirement` comes from useTripTravelRequirements.
  const docRequired = Boolean(travelRequirement?.international);
  // seatSelections arrives either as a flat { seatId: data } object or as the
  // per-segment array produced by the seat map step ([{ '14A': {...} }, {}]).
  // Flatten to the picked seat designators; if the customer already chose
  // seats on the live seat map, the preference picker below is redundant.
  const seatSegments = (Array.isArray(seatSelections) ? seatSelections : [seatSelections])
    .filter((segment) => segment && typeof segment === 'object');
  const pickedSeatIds = seatSegments.flatMap((segment) => Object.keys(segment));
  const hasPickedSeats = pickedSeatIds.length > 0;
  // Some segments may have had no seat map (airline/route not supported) —
  // keep the preference picker visible so the concierge can still request
  // seats for those legs. Hide it only when EVERY segment has picks.
  const segmentsWithSeats = seatSegments.filter((segment) => Object.keys(segment).length > 0).length;
  const allSegmentsHaveSeats = segmentsWithSeats > 0 && segmentsWithSeats === seatSegments.length;
  // Apply the default cycle (Window/Middle/Aisle | Aisle/Middle/Window,
  // repeating every 6) to any incoming passenger that doesn't already
  // carry an explicit `seat_preference`. This means brand-new
  // passengers start with a thoughtful default, while passengers
  // restored from a saved profile keep whatever they had stored.
  const [currentPassengers, setCurrentPassengers] = useState(() =>
    passengers.map((p, i) => ({
      ...p,
      seat_preference:
        normalizeSeatPreference(p?.seat_preference || p?.seatPreference) ||
        getDefaultSeatPreference(i),
    }))
  );

  const [savedPassengers, setSavedPassengers] = useState([]);
  const [selectedSavedPassengerIds, setSelectedSavedPassengerIds] = useState({});
  const [hasAutoPrefilledRef, setHasAutoPrefilled] = useState(false);
  const [loyaltyAccounts, setLoyaltyAccounts] = useState([]);

  useEffect(() => {
    const fetchSavedPassengers = async () => {
      try {
        const user = await UserEntity.me();
        const data = await SavedPassenger.findByUserId(user.id);
        setSavedPassengers(data);
      } catch (error) {
        console.error("Failed to fetch saved passengers:", error);
        console.error("Error message:", error?.message);
        console.error("Error stack:", error?.stack);
      }
    };
    const fetchLoyaltyAccounts = async () => {
      try {
        const accounts = await UserLoyaltyAccount.list();
        setLoyaltyAccounts(accounts);
      } catch (error) {
        // Wallet is a nice-to-have at checkout — never block the form on it.
        console.error("Failed to fetch loyalty accounts:", error);
      }
    };
    fetchSavedPassengers();
    fetchLoyaltyAccounts();
  }, []);

  // Every marketing carrier on the itinerary, outbound first — the order
  // decides which wallet entry wins when the trip mixes airlines.
  const bookingCarrierCodes = useMemo(() => {
    const codes = [];
    for (const slice of selectedFlight?.slices || []) {
      for (const segment of slice?.segments || []) {
        const code = segment?.marketing_carrier?.iata_code;
        if (code) codes.push(code.toString().toUpperCase());
      }
    }
    return [...new Set(codes)];
  }, [selectedFlight]);

  const matchedLoyalty = useMemo(() => {
    if (loyaltyAccounts.length === 0 || bookingCarrierCodes.length === 0) return null;
    for (const code of bookingCarrierCodes) {
      const account = loyaltyAccounts.find((a) =>
        getProgramByValue(a.program_key)?.iata?.includes(code)
      );
      if (account) {
        return { account, program: getProgramByValue(account.program_key) };
      }
    }
    return null;
  }, [loyaltyAccounts, bookingCarrierCodes]);

  // Auto-apply the wallet number to passenger 1 (the account owner). A
  // number already present for a program that credits to this itinerary's
  // carrier is respected; a leftover number for an unrelated airline is
  // replaced. Re-runs when a saved passenger is selected (which overwrites
  // the FF fields with the saved profile's single generic value).
  useEffect(() => {
    if (!matchedLoyalty) return;
    const { account } = matchedLoyalty;
    setCurrentPassengers((prev) => {
      const p0 = prev[0];
      if (!p0) return prev;
      if (
        p0.frequent_flyer_program === account.program_key &&
        p0.frequent_flyer_number === account.account_number
      ) return prev;
      const currentProgram = getProgramByValue(p0.frequent_flyer_program);
      const currentMatchesBooking = currentProgram?.iata?.some((c) => bookingCarrierCodes.includes(c));
      if (p0.frequent_flyer_number && currentMatchesBooking) return prev;
      const next = [...prev];
      next[0] = {
        ...p0,
        frequent_flyer_program: account.program_key,
        frequent_flyer_number: account.account_number,
      };
      return next;
    });
  }, [matchedLoyalty, selectedSavedPassengerIds, bookingCarrierCodes]);

  // Derived, not stored — the badge disappears the moment the customer
  // edits either field, so it can never claim an application that isn't
  // exactly what will be submitted.
  const loyaltyAutoApplied =
    matchedLoyalty &&
    currentPassengers[0]?.frequent_flyer_program === matchedLoyalty.account.program_key &&
    currentPassengers[0]?.frequent_flyer_number === matchedLoyalty.account.account_number;

  // Auto-prefill the most recent saved passenger when form is initially empty
  useEffect(() => {
    if (
      !hasAutoPrefilledRef &&
      savedPassengers.length > 0 &&
      currentPassengers.length > 0 &&
      (!currentPassengers[0].first_name || currentPassengers[0].first_name.trim() === '')
    ) {
      const mostRecentPassenger = savedPassengers[0]; // sorted by created_at desc
      console.log('Auto-prefilling first passenger with their most recent saved traveler');
      handleSelectSavedPassenger(0, mostRecentPassenger.id);
      setHasAutoPrefilled(true);
    }
  }, [savedPassengers, currentPassengers, hasAutoPrefilledRef]);

  const updatePassenger = (index, field, value) => {
    const newPassengers = [...currentPassengers];
    newPassengers[index] = { ...newPassengers[index], [field]: value };
    setCurrentPassengers(newPassengers);
  };

  const handleSelectSavedPassenger = (index, passengerId) => {
    const selected = savedPassengers.find((p) => p.id === passengerId);
    if (selected) {
      const newPassengers = [...currentPassengers];
      // Preserve special requests already typed if the saved passenger
      // doesn't carry their own. Everything else is overwritten with the
      // saved record so the form mirrors the stored profile exactly.
      newPassengers[index] = {
        ...newPassengers[index],
        title: selected.title || '',
        first_name: selected.firstName || selected.givenName || '',
        middle_name: selected.middleName || selected.middle_name || '',
        last_name: selected.lastName || selected.familyName || '',
        date_of_birth: formatDateForInput(selected.bornOn),
        gender: airlineGender(selected.gender),
        passport_number: selected.passportNumber || '',
        passport_expiry: formatDateForInput(selected.passportExpiryDate),
        nationality: countryPickerValue(selected.nationality),
        passport_country: countryPickerValue(selected.passportCountry || selected.passport_country),
        phone: selected.phoneNumber || '',
        email: selected.email || '',
        frequent_flyer_program: selected.frequentFlyerProgram || selected.frequent_flyer_program || '',
        frequent_flyer_number: selected.frequentFlyerNumber || '',
        known_traveler_number: selected.knownTravelerNumber || '',
        special_requests: selected.specialRequests || newPassengers[index].special_requests || '',
        // Seat preference behaviour: if the saved profile carries one,
        // restore it; otherwise keep whatever was already in the form
        // (cycle default or user choice) so the picker never goes blank.
        seat_preference:
          normalizeSeatPreference(selected.seatPreference || selected.seat_preference) ||
          newPassengers[index].seat_preference ||
          getDefaultSeatPreference(index)
      };
      setCurrentPassengers(newPassengers);
      setSelectedSavedPassengerIds({ ...selectedSavedPassengerIds, [index]: passengerId });
    }
  };

  const handleClearSavedPassenger = (index) => {
    const newPassengers = [...currentPassengers];
    newPassengers[index] = {
      title: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      passport_number: '',
      passport_expiry: '',
      nationality: '',
      passport_country: '',
      phone: '',
      email: '',
      frequent_flyer_program: '',
      frequent_flyer_number: '',
      known_traveler_number: '',
      special_requests: '',
      wheelchair_assistance: null,
      // Reset to the cycle default rather than blank so the seat
      // preference picker always shows an opinionated starting value.
      seat_preference: getDefaultSeatPreference(index)
    };
    setCurrentPassengers(newPassengers);
    const newSelectedIds = { ...selectedSavedPassengerIds };
    delete newSelectedIds[index];
    setSelectedSavedPassengerIds(newSelectedIds);
  };

  const handlePassengerFormSubmit = (e) => {
    e.preventDefault();
    if (isFormValid && onComplete) {
      onComplete(currentPassengers, selectedSavedPassengerIds);
    }
  };

  // Gender is required because Duffel's order-create API rejects passengers
  // without a gender value (`male` / `female`), and our concierge-bot
  // instructions in server/tommy-concierge.js already promise customers
  // it's a required field. Pre-fix this was optional → ~4.3% of historical
  // passenger rows had empty gender and would have failed at ticketing.
  // WESX5W (Tanis Buchanan, 2026-06-18) surfaced the gap.
  //
  // A special request that mentions a wheelchair brings up the airline's four
  // assistance questions, and all four are required like the other checkout
  // questions: the airline can't arrange help without them (2026-10-05).
  //
  // International trips also need the full travel document (nationality,
  // issuing country, number, expiry), and a passport that lasts past the last
  // flight; the airline can't board a traveler without one (2026-11-02).
  const isFormValid = currentPassengers.every(
    (p) => p.first_name && p.last_name && p.date_of_birth && p.phone && airlineGender(p.gender)
      && (!mentionsWheelchair(p.special_requests) || wheelchairComplete(p.wheelchair_assistance))
      && missingTravelDocument(p, travelRequirement).length === 0,
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="space-y-6"
    >
      {/* Header — eyebrow + light title + sub. Mirrors the Trips-page
          masthead pattern (small caps eyebrow, light tracking-tight
          headline, dim caption) so the modal feels like part of the
          same product surface rather than a leftover form panel. */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(228,35%,58%)]/85 font-medium">
          Step 2 · Travelers
        </p>
        <h3 className="text-2xl md:text-[28px] font-light text-white tracking-tight leading-none">
          Passenger details
        </h3>
        <p className="text-white/60 text-[13px] font-normal leading-snug max-w-md">
          Enter info exactly as it appears on the passport or government ID.
        </p>

        {hasPickedSeats && (
          <div className="flex flex-wrap items-center gap-1.5 pt-2">
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/35 font-normal mr-1">Seats</span>
            {pickedSeatIds.map((seatId) => (
              <span
                key={seatId}
                className="text-[11px] font-normal text-[hsl(228,30%,72%)] bg-[hsl(228,40%,38%)]/[0.16] border border-[hsl(228,35%,58%)]/20 px-2 py-0.5 rounded-full tabular-nums"
              >
                {seatId}
              </span>
            ))}
          </div>
        )}
      </div>

      <form
        onSubmit={handlePassengerFormSubmit}
        className="space-y-5"
        noValidate
        autoComplete="on"
      >
        {currentPassengers.map((passenger, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.06 }}
            className="bg-white/[0.025] border border-white/10 rounded-3xl overflow-hidden"
          >
            {/* Card header — numbered badge in the royal-blue accent
                + saved-passenger picker pill on the right. Stacks
                vertically below sm so the picker gets full width on
                phones instead of crushing next to the title. */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-6 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-full bg-[hsl(228,40%,38%)]/[0.16] border border-[hsl(228,35%,58%)]/20 flex items-center justify-center">
                  <span className="text-[hsl(228,30%,72%)] text-[11px] font-medium tabular-nums">{index + 1}</span>
                </div>
                <h4 className="text-white font-normal text-[15px] tracking-tight">Passenger {index + 1}</h4>
              </div>
              {savedPassengers.length > 0 && (
                <div className="flex items-center gap-1.5 w-full sm:w-auto">
                  <Select
                    value={selectedSavedPassengerIds[index] || ''}
                    onValueChange={(id) => handleSelectSavedPassenger(index, id)}
                  >
                    <SelectTrigger
                      className={cn(
                        'bg-white/[0.03] border-white/10 text-white h-9 rounded-full text-xs font-normal px-3.5 transition-colors hover:bg-white/[0.06] data-[placeholder]:text-white/45 data-[state=open]:bg-white/[0.06] data-[state=open]:border-[hsl(228,35%,58%)]/30 sm:w-[200px]'
                      )}
                    >
                      <SelectValue placeholder="Fill from saved…" />
                    </SelectTrigger>
                    <SelectContent>
                      {savedPassengers.map((sp) => (
                        <SelectItem key={sp.id} value={sp.id}>
                          {sp.nickname ? `${sp.nickname} (${sp.firstName} ${sp.lastName})` : `${sp.firstName} ${sp.lastName}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedSavedPassengerIds[index] && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleClearSavedPassenger(index)}
                      className="h-9 w-9 rounded-full text-white/55 hover:text-white hover:bg-white/[0.06] flex-shrink-0"
                      title="Clear saved passenger data"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Card body — four sections separated by hairline eyebrow
                rules. Each section uses a 1-col grid on mobile and a
                2-col grid from sm up so two short fields (e.g.
                phone/email) sit side-by-side without wrapping. */}
            <div className="p-5 sm:p-6 space-y-7">

              {/* Identity */}
              <Section title="Identity">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <Field label="Title">
                    <Select value={passenger.title} onValueChange={(value) => updatePassenger(index, 'title', value)}>
                      <SelectTrigger className={TRIGGER_BASE}>
                        <SelectValue placeholder="Select title" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mr">Mr</SelectItem>
                        <SelectItem value="Ms">Ms</SelectItem>
                        <SelectItem value="Mrs">Mrs</SelectItem>
                        <SelectItem value="Dr">Dr</SelectItem>
                        <SelectItem value="Prof">Prof</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Gender" required>
                    <Select value={passenger.gender} onValueChange={(value) => updatePassenger(index, 'gender', value)}>
                      <SelectTrigger className={TRIGGER_BASE}>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="First name" required>
                    <Input
                      value={passenger.first_name}
                      onChange={(e) => updatePassenger(index, 'first_name', e.target.value)}
                      className={FIELD_BASE}
                      placeholder="As on passport / ID"
                      autoComplete="given-name"
                      autoCapitalize="words"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                    />
                  </Field>
                  <Field label="Middle name">
                    <Input
                      value={passenger.middle_name || ''}
                      onChange={(e) => updatePassenger(index, 'middle_name', e.target.value)}
                      className={FIELD_BASE}
                      placeholder="If on passport / ID"
                      autoComplete="additional-name"
                      autoCapitalize="words"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                    />
                  </Field>
                  <Field label="Last name" required>
                    <Input
                      value={passenger.last_name}
                      onChange={(e) => updatePassenger(index, 'last_name', e.target.value)}
                      className={FIELD_BASE}
                      placeholder="As on passport / ID"
                      autoComplete="family-name"
                      autoCapitalize="words"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                    />
                  </Field>
                  <Field label="Date of birth" required>
                    <DobInput
                      value={passenger.date_of_birth || ''}
                      onChange={(iso) => updatePassenger(index, 'date_of_birth', iso)}
                      className={FIELD_BASE}
                      aria-label={`Date of birth for passenger ${index + 1}`}
                    />
                  </Field>
                </div>
              </Section>

              {/* Travel document */}
              <Section title="Travel document">
                {docRequired && (
                  <p className="text-[13px] text-white/55 font-normal leading-snug -mt-1">
                    Required for international trips. Enter it exactly as it appears on the passport.
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <Field label="Nationality" required={docRequired}>
                    <CountrySelect
                      value={passenger.nationality}
                      onChange={(name) => updatePassenger(index, 'nationality', name)}
                      placeholder="Select country"
                      ariaLabel={`Nationality for passenger ${index + 1}`}
                    />
                  </Field>
                  <Field label="Passport issued by" required={docRequired}>
                    <CountrySelect
                      value={passenger.passport_country}
                      onChange={(name) => updatePassenger(index, 'passport_country', name)}
                      placeholder="Select country"
                      ariaLabel={`Passport issuing country for passenger ${index + 1}`}
                    />
                  </Field>
                  <Field label={docRequired ? 'Passport number' : 'Passport / ID number'} required={docRequired}>
                    <Input
                      value={passenger.passport_number}
                      onChange={(e) => updatePassenger(index, 'passport_number', e.target.value)}
                      className={FIELD_BASE}
                      placeholder={docRequired ? 'Passport number' : 'Passport or ID number'}
                      autoComplete="off"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                    />
                  </Field>
                  <Field label="Passport expiration" required={docRequired}>
                    <Input
                      type="date"
                      value={passenger.passport_expiry || ''}
                      onChange={(e) => updatePassenger(index, 'passport_expiry', e.target.value)}
                      className={DATE_FIELD}
                      aria-label={`Passport expiration for passenger ${index + 1}`}
                    />
                  </Field>
                </div>
                <TravelerTravelNotes passenger={passenger} requirement={travelRequirement} />
              </Section>

              {/* Contact */}
              <Section title="Contact">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <Field label="Phone" required>
                    <Input
                      type="tel"
                      inputMode="tel"
                      value={passenger.phone}
                      onChange={(e) => updatePassenger(index, 'phone', e.target.value)}
                      className={FIELD_BASE}
                      placeholder="+1 234 567 8900"
                      autoComplete="tel"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                    />
                  </Field>
                  <Field label="Email">
                    <Input
                      type="email"
                      inputMode="email"
                      value={passenger.email}
                      onChange={(e) => updatePassenger(index, 'email', e.target.value)}
                      className={FIELD_BASE}
                      placeholder="passenger@email.com"
                      autoComplete="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                    />
                  </Field>
                </div>
              </Section>

              {/* Loyalty & preferences */}
              <Section title="Loyalty & preferences">
                {index === 0 && loyaltyAutoApplied && (
                  <div className="flex items-center gap-3 rounded-2xl border border-[hsl(228,35%,58%)]/15 bg-[hsl(228,40%,38%)]/[0.08] px-3.5 py-2.5">
                    <TailLogo iata={matchedLoyalty.account.airline_iata} size="sm" />
                    <p className="text-[13px] leading-snug text-white/60 font-normal">
                      <span className="text-white/85">{matchedLoyalty.program?.label || 'Frequent flyer number'}</span>
                      {' '}applied from your profile — this trip credits to your account.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <Field label="Frequent flyer program" className="sm:col-span-2">
                    <Select
                      value={passenger.frequent_flyer_program || ''}
                      onValueChange={(value) => updatePassenger(index, 'frequent_flyer_program', value)}
                    >
                      <SelectTrigger className={TRIGGER_BASE}>
                        <SelectValue placeholder="Select program…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {AIRLINE_PROGRAMS.map((program) => (
                          <SelectItem key={program.value} value={program.value}>{program.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Frequent flyer #">
                    <Input
                      value={passenger.frequent_flyer_number}
                      onChange={(e) => updatePassenger(index, 'frequent_flyer_number', e.target.value)}
                      className={FIELD_BASE}
                      placeholder="e.g. AA1234567"
                      autoComplete="off"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                    />
                  </Field>
                  <Field label="Known traveler #">
                    <Input
                      inputMode="numeric"
                      value={passenger.known_traveler_number}
                      onChange={(e) => updatePassenger(index, 'known_traveler_number', e.target.value)}
                      className={FIELD_BASE}
                      placeholder="KTN for TSA PreCheck"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                    />
                  </Field>
                </div>
                <Field label="Special requests" className="mt-4">
                  <Textarea
                    value={passenger.special_requests}
                    onChange={(e) => updatePassenger(index, 'special_requests', e.target.value)}
                    className={TEXTAREA_BASE}
                    placeholder="Special meals, wheelchair assistance, etc."
                    rows={3}
                    enterKeyHint="done"
                  />
                </Field>
                {mentionsWheelchair(passenger.special_requests) && (
                  <div className="mt-4 rounded-2xl border border-[hsl(228,35%,58%)]/15 bg-[hsl(228,40%,38%)]/[0.06] px-3.5 py-3.5 space-y-4">
                    <div className="flex items-start gap-2.5">
                      <Accessibility className="w-4 h-4 mt-0.5 text-[hsl(228,35%,58%)] flex-shrink-0" aria-hidden />
                      <div className="min-w-0">
                        <p className="text-[13.5px] font-medium text-white leading-snug">Wheelchair assistance</p>
                        <p className="mt-1 text-[12.5px] leading-relaxed font-normal text-white/60">
                          The airline needs these answers to arrange assistance. You can change them anytime from your trip.
                        </p>
                      </div>
                    </div>
                    <WheelchairQuestions
                      value={passenger.wheelchair_assistance}
                      onChange={(answers) => updatePassenger(index, 'wheelchair_assistance', answers)}
                      idPrefix={`pax-${index}-wheelchair`}
                    />
                    {!wheelchairComplete(passenger.wheelchair_assistance) && (
                      <p className="text-[12px] font-normal text-[hsl(228,30%,72%)]">Answer all four to continue.</p>
                    )}
                  </div>
                )}
              </Section>

              {/* Seat preference — defaults seed a sensible W/M/A
                  pattern so groups travelling together cluster
                  naturally across the aisle. Concierge note below
                  the picker explains how the preference is honored
                  (and what to do if the fare doesn't include
                  complimentary seats). Hidden only when the customer
                  picked exact seats on the live seat map for EVERY
                  segment — if some legs had no seat map (airline /
                  route limitation), the preference still applies to
                  those legs. */}
              {!allSegmentsHaveSeats && (
              <Section title="Seat preference">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 items-start">
                  <Field label="Preferred seat">
                    <Select
                      value={passenger.seat_preference || getDefaultSeatPreference(index)}
                      onValueChange={(value) => updatePassenger(index, 'seat_preference', value)}
                    >
                      <SelectTrigger className={TRIGGER_BASE} aria-label={`Seat preference for passenger ${index + 1}`}>
                        <SelectValue placeholder="Select preference" />
                      </SelectTrigger>
                      <SelectContent>
                        {SEAT_PREFERENCE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="flex items-start gap-2.5 rounded-2xl border border-[hsl(228,35%,58%)]/15 bg-[hsl(228,40%,38%)]/[0.06] px-3.5 py-3 mt-1">
                  <Armchair className="w-3.5 h-3.5 mt-0.5 text-[hsl(228,35%,58%)] flex-shrink-0" />
                  <p className="text-[13px] leading-relaxed text-white/60 font-normal">
                    {hasPickedSeats
                      ? 'Some of your flights don\u2019t offer a live seat map — that depends on the airline and route, not your booking. For those legs, our'
                      : 'Not every airline or route offers a live seat map — if you weren\u2019t shown one during booking, that\u2019s the airline, not your booking. Our'}
                    {' '}concierge team uses these preferences to seat your group together and as close to the front of the aircraft as the fare allows. If your fare doesn't include complimentary seat selection, reach out to our concierge to <span className="text-white/80">purchase seats</span>, or wait until check-in and the airline will assign seats automatically.
                  </p>
                </div>
              </Section>
              )}
            </div>
          </motion.div>
        ))}

        {/* Footer — privacy reassurance on the left, primary CTA on
            the right. Stacks reverse on mobile so the button (which
            requires the most attention) sits at the bottom of the
            viewport, within thumb reach. The CTA is full-width on
            phones for the same reason. */}
        <div className="pt-2">
          <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3 pt-5 border-t border-white/[0.06]">
            <p className="text-white/35 text-[11px] font-normal flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-[hsl(228,35%,58%)]/80" />
              Encrypted · required <span className="text-[hsl(228,30%,72%)]">*</span>
            </p>
            <Button
              type="submit"
              disabled={!isFormValid || isProcessing}
              className={cn(
                'h-11 rounded-full px-6 text-[13px] tracking-wide w-full sm:w-auto',
                BRAND_PRIMARY_BTN_CLASS,
                'disabled:bg-white/[0.06] disabled:text-white/30 disabled:shadow-none disabled:border-transparent disabled:cursor-not-allowed disabled:opacity-100'
              )}
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing…
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Continue to payment
                </span>
              )}
            </Button>
          </div>
        </div>
      </form>
    </motion.div>
  );
}

PassengerForm.propTypes = {
  travelRequirement: travelRequirementShape,
};
